/**
 * React hooks for the analytics SDK.
 *
 * Thin wrappers around the core SDK for ergonomic use in components.
 */

import { useFeatureFlagVariantKey } from '@posthog/react';
import { useCallback, useEffect } from 'react';
import { analytics } from './sdk';
import type { AnalyticsEvent } from './events';
import type { FeatureArea } from './feature-areas';

const EXPOSED_KEY = 'reklaim_experiments_exposed';

function readExposedMap(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(EXPOSED_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, number>)
      : {};
  } catch {
    return {};
  }
}

/**
 * Returns true if this is the first exposure for experimentKey+variant in this session.
 * On storage failure, returns true so we still emit exposure (fail-open).
 */
function markExposed(experimentKey: string, variant: string): boolean {
  try {
    const stored = readExposedMap();
    const key = `${experimentKey}:${variant}`;
    if (stored[key]) return false;
    stored[key] = Date.now();
    sessionStorage.setItem(EXPOSED_KEY, JSON.stringify(stored));
    return true;
  } catch {
    return true;
  }
}

/**
 * Returns a stable callback for firing analytics events.
 *
 * Usage:
 *   const track = useTrack();
 *   track({ name: 'survey_completed', properties: { survey_id: '123', completion_time_ms: 45000 } });
 */
export function useTrack() {
  return useCallback((event: AnalyticsEvent) => {
    analytics.trackEvent(event);
  }, []);
}

/**
 * Returns the value of a PostHog feature flag (reactive).
 *
 * Uses `@posthog/react` so components re-render when flags finish loading
 * (`onFeatureFlags`), not only on synchronous reads.
 */
export function useFeatureFlag(key: string): string | boolean | undefined {
  return useFeatureFlagVariantKey(key);
}

/**
 * Standard experiment hook.
 *
 * Evaluates a PostHog feature flag and fires `experiment_viewed` once per
 * session per (experiment_key, variant) after the real variant is known
 * (not while PostHog is still loading / `undefined`, and not when the flag
 * evaluates to `false`).
 *
 * Usage:
 *   const variant = useExperiment('onboarding-v2');
 *   if (variant === 'new-flow') { ... }
 */
export function useExperiment(experimentKey: string): string | boolean | undefined {
  const variant = useFeatureFlag(experimentKey);

  useEffect(() => {
    // PostHog returns `false` when the flag is off or unknown — not a multivariate key.
    if (variant === undefined || variant === false) return;
    const isFirstExposure = markExposed(experimentKey, String(variant));
    if (isFirstExposure) {
      analytics.trackEvent({
        name: 'experiment_viewed',
        properties: { experiment_key: experimentKey, variant: String(variant) },
      });
    }
  }, [variant, experimentKey]);

  return variant;
}

export type UseCrossSellPromptTrackingOptions = {
  /** When false, skips `cross_sell_prompt_shown` until true (e.g. subscription state resolved). */
  enabled?: boolean;
};

/**
 * Tracks cross-sell prompt impressions and CTA clicks (REK-1298).
 * Fires `cross_sell_prompt_shown` once when enabled and deps settle; returns a
 * stable callback for `cross_sell_prompt_clicked`.
 */
export function useCrossSellPromptTracking(
  sourceFeature: FeatureArea,
  targetFeature: FeatureArea,
  options?: UseCrossSellPromptTrackingOptions,
) {
  const enabled = options?.enabled !== false;

  useEffect(() => {
    if (!enabled) return;
    analytics.trackEvent({
      name: 'cross_sell_prompt_shown',
      properties: { source_feature: sourceFeature, target_feature: targetFeature },
    });
  }, [sourceFeature, targetFeature, enabled]);

  return useCallback(() => {
    analytics.trackEvent({
      name: 'cross_sell_prompt_clicked',
      properties: { source_feature: sourceFeature, target_feature: targetFeature },
    });
  }, [sourceFeature, targetFeature]);
}
